import { Routes, Route, useLocation } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import { AnimatePresence } from 'motion/react';
import Layout from './components/Layout';

// Lazy load page components for better performance
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Contact = lazy(() => import('./pages/Contact'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const IndustryDetail = lazy(() => import('./pages/IndustryDetail'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const Technology = lazy(() => import('./pages/Technology'));
const DeveloperTools = lazy(() => import('./pages/tools/DeveloperTools'));
const ImageTools = lazy(() => import('./pages/tools/ImageTools'));
const UtilityTools = lazy(() => import('./pages/tools/UtilityTools'));
const NotFound = lazy(() => import('./pages/NotFound'));

// import Loader from './components/Loader';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export default function App() {
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();

  // 1. Initial GA4 Script Injection
  useEffect(() => {
    const canLoadAnalytics = () => {
      try {
        const c = JSON.parse(localStorage.getItem('cookie_consent') || '{}');
        return !!c.analytics;
      } catch (_) {
        return false;
      }
    };

    if (canLoadAnalytics() && !window.gtag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-JGT1LM7YJ8';
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };

      window.gtag('js', new Date());
      window.gtag('config', 'G-JGT1LM7YJ8');
    }
  }, []);

  // 2. Track Page Views on Route Changes
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-JGT1LM7YJ8', {
        page_path: location.pathname + location.search
      });
    }
  }, [location]);

  return (
    <>
      <AnimatePresence mode="wait">
        {/* {isLoading && <Loader key="loader" onComplete={() => setIsLoading(false)} />} */}
      </AnimatePresence>

      <Suspense fallback={<div className="min-h-screen bg-[#050505]" />}>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="about-us" element={<About />} />
            <Route path="services" element={<Services />} />
            <Route path="services/:id" element={<ServiceDetail />} />
            <Route path="industries/:id" element={<IndustryDetail />} />
            <Route path="portfolio" element={<Portfolio />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/:slug" element={<BlogDetail />} />
            <Route path="technology" element={<Technology />} />
            <Route path="contact-us" element={<Contact />} />
            <Route path="tools/developer" element={<DeveloperTools />} />
            <Route path="tools/json-formatter" element={<DeveloperTools toolId="json" />} />
            <Route path="tools/case-converter" element={<DeveloperTools toolId="case" />} />
            <Route path="tools/prompt-to-url" element={<DeveloperTools toolId="prompt" />} />
            <Route path="tools/bcrypt-generator" element={<DeveloperTools toolId="bcrypt" />} />

            <Route path="tools/image" element={<ImageTools />} />
            <Route path="tools/image-compressor" element={<ImageTools toolId="compress" />} />
            <Route path="tools/image-enhancer" element={<ImageTools toolId="compress" />} />

            <Route path="tools/utility" element={<UtilityTools />} />
            <Route path="tools/typing-test" element={<UtilityTools toolId="typing" />} />
            <Route path="tools/color-generator" element={<UtilityTools toolId="color" />} />
            <Route path="tools/password-generator" element={<UtilityTools toolId="password" />} />
            <Route path="tools/what-is-my-ip" element={<UtilityTools toolId="ip" />} />
            <Route path="tools/live-translate" element={<UtilityTools toolId="translate" />} />

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}
